import { useMemo } from "react";
import { Chip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "components/Tables/DataTable/DataTable";
import CopyButton from "./CopyButton";

const DNS_GROUP_COLORS: Record<string, "info" | "default" | "secondary"> = {
  DKIM: "info",
  SPF: "secondary",
  DMARC: "default",
};

interface DnsRow {
  group: string;
  type: string;
  host: string;
  value: string;
  id: string;
}

interface DnsRecordsTableProps {
  identity: any;
  copiedField: string | null;
  onCopy: (value: string, fieldId: string) => void;
}

export default function DnsRecordsTable({ identity, copiedField, onCopy }: DnsRecordsTableProps) {
  const { dkimTokens, spfRecord, dmarcRecord } = identity;

  const rows: DnsRow[] = useMemo(() => {
    const out: DnsRow[] = [];

    if (dkimTokens) {
      dkimTokens.forEach((token: any, i: number) => {
        out.push({
          group: "DKIM",
          type: token.type,
          host: token.host,
          value: token.value,
          id: `dkim-${i}`,
        });
      });
    }

    if (spfRecord) {
      out.push({
        group: "SPF",
        type: spfRecord.type,
        host: spfRecord.host,
        value: spfRecord.value,
        id: "spf",
      });
    }

    if (dmarcRecord) {
      out.push({
        group: "DMARC",
        type: dmarcRecord.type,
        host: dmarcRecord.host,
        value: dmarcRecord.value,
        id: "dmarc",
      });
    }

    return out;
  }, [dkimTokens, spfRecord, dmarcRecord]);

  const columns = useMemo(
    () => [
      {
        Header: "Record",
        accessor: "group",
        disableSortBy: true,
        width: 100,
        Cell: ({ value }: any) => (
          <Chip
            label={value}
            size="small"
            color={DNS_GROUP_COLORS[value] ?? "default"}
            variant="outlined"
          />
        ),
      },
      {
        Header: "Type",
        accessor: "type",
        disableSortBy: true,
        width: 100,
        Cell: ({ row, value }: any) => (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <MDTypography variant="body2" fontFamily="monospace">
              {value}
            </MDTypography>
            <CopyButton
              value={value}
              fieldId={`${row.original.id}-type`}
              copiedField={copiedField}
              onCopy={onCopy}
            />
          </MDBox>
        ),
      },
      {
        Header: "Host / Name",
        accessor: "host",
        disableSortBy: true,
        Cell: ({ row, value }: any) => (
          <MDBox display="flex" alignItems="flex-start" gap={0.5}>
            <MDTypography
              variant="body2"
              fontFamily="monospace"
              sx={{ wordBreak: "break-all", flex: 1, minWidth: 0 }}
            >
              {value}
            </MDTypography>
            <CopyButton
              value={value}
              fieldId={`${row.original.id}-host`}
              copiedField={copiedField}
              onCopy={onCopy}
            />
          </MDBox>
        ),
      },
      {
        Header: "Value",
        accessor: "value",
        disableSortBy: true,
        Cell: ({ row, value }: any) => (
          <MDBox display="flex" alignItems="flex-start" gap={0.5}>
            <MDTypography
              variant="body2"
              fontFamily="monospace"
              sx={{ wordBreak: "break-all", flex: 1, minWidth: 0 }}
            >
              {value}
            </MDTypography>
            <CopyButton
              value={value}
              fieldId={`${row.original.id}-value`}
              copiedField={copiedField}
              onCopy={onCopy}
            />
          </MDBox>
        ),
      },
    ],
    [copiedField, onCopy]
  );

  if (rows.length === 0) {
    return (
      <MDTypography variant="body2">
        No DNS records available yet. Save your configuration to generate records.
      </MDTypography>
    );
  }

  return <DataTable table={{ columns, rows }} showTotalEntries={false} />;
}
